/**
 * A voice note's waveform: what the recorder samples, what the slot is sent, and what a bubble
 * draws.
 *
 * The wire carries at most 64 integers in 0..31 (`waveform` on the voice slot; the backend refuses
 * anything else). The recorder takes one reading every tick as an RMS level in 0..1 and keeps them
 * all; `wireWaveform` folds that series down to the wire's shape, and `barsOf` folds any series to
 * the number of bars a surface draws.
 */

export const WAVEFORM_MAX_VALUE = 31;
export const WAVEFORM_MAX_LENGTH = 64;

/**
 * An RMS level (0..1) as a wire value. Speech sits around 0.02–0.3 RMS, so the scale is logarithmic
 * over -50..0 dBFS, the phone's metering range compressed by ten decibels at the quiet end.
 */
export function levelOfRms(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  const fraction = Math.min(1, Math.max(0, (db + 50) / 50));
  return Math.round(fraction * WAVEFORM_MAX_VALUE);
}

/** `series` folded to `count` buckets by their maximum. An empty series stays empty. */
export function barsOf(series: readonly number[], count: number): number[] {
  if (series.length === 0 || count <= 0) return [];
  if (series.length <= count) return [...series];
  const out: number[] = [];
  for (let bucket = 0; bucket < count; bucket += 1) {
    const from = Math.floor((bucket * series.length) / count);
    const to = Math.max(from + 1, Math.floor(((bucket + 1) * series.length) / count));
    let peak = 0;
    for (let index = from; index < to; index += 1) peak = Math.max(peak, series[index] ?? 0);
    out.push(peak);
  }
  return out;
}

/** What the slot is sent: at most 64 values, each an integer in 0..31, or `null` for nothing. */
export function wireWaveform(levels: readonly number[]): number[] | null {
  const bars = barsOf(levels, WAVEFORM_MAX_LENGTH).map((value) =>
    Math.min(WAVEFORM_MAX_VALUE, Math.max(0, Math.round(value))),
  );
  return bars.length === 0 ? null : bars;
}

/** `m:ss` for a duration in seconds. */
export function durationLabel(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

/** The three speeds a voice note plays at, in the order the control steps through them. */
export const VOICE_SPEEDS = [1, 1.5, 2] as const;
export type VoiceSpeed = (typeof VOICE_SPEEDS)[number];

export function nextVoiceSpeed(speed: VoiceSpeed): VoiceSpeed {
  const index = VOICE_SPEEDS.indexOf(speed);
  return VOICE_SPEEDS[(index + 1) % VOICE_SPEEDS.length] ?? 1;
}
